import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxOpen, faUser } from "@fortawesome/free-solid-svg-icons";
import Cookie from "cookie-universal";
import { Axios } from "../../../API/Axios";
import { useLanguage } from "../../../Context/LanguageContext";
import "./Orders.css";

const cookieJar = Cookie();

const STATUS_CLASS = {
    pending: "status-pending",
    processing: "status-processing",
    shipped: "status-shipped",
    delivered: "status-delivered",
    cancelled: "status-cancelled",
};

export default function Orders ()
{
    const { t, lang } = useLanguage();
    const [ orders, setOrders ] = useState( [] );
    const [ loading, setLoading ] = useState( true );

    const token = cookieJar.get( "ecommerce" );

    useEffect( () =>
    {
        if ( !token )
        {
            setLoading( false );
            return;
        }
        Axios.get( "/orders" )
            .then( ( res ) => setOrders( res.data ) )
            .catch( () => setOrders( [] ) )
            .finally( () => setLoading( false ) );
    }, [ token ] );

    if ( loading )
    {
        return <div className="orders-page" />;
    }

    if ( !token )
    {
        return (
            <div className="orders-page">
                <div className="orders-guest-card">
                    <div className="orders-guest-icon">
                        <FontAwesomeIcon icon={ faUser } />
                    </div>
                    <h1>{ t( 'orders.loginRequiredTitle' ) }</h1>
                    <Link to="/auth" className="orders-login-cta">{ t( 'orders.loginCta' ) }</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="orders-page">
            <div className="orders-container">
                <div className="orders-page-header">
                    <h1>
                        <FontAwesomeIcon icon={ faBoxOpen } />
                        { t( 'orders.pageTitle' ) }
                    </h1>
                    <Link to="/profile" className="orders-back-link">{ t( 'orders.backToProfile' ) }</Link>
                </div>

                { !orders || orders.length === 0 ? (
                    <div className="orders-empty-state">
                        <FontAwesomeIcon icon={ faBoxOpen } />
                        <p>{ t( 'orders.noOrders' ) }</p>
                        <Link to="/shop" className="orders-login-cta">{ t( 'orders.browseShop' ) }</Link>
                    </div>
                ) : (
                    <div className="orders-list">
                        { orders.map( ( order ) =>
                        {
                            const statusText = t( `orders.status.${ order.status }` );
                            const statusClass = STATUS_CLASS[ order.status ] || STATUS_CLASS.pending;
                            return (
                                <Link to={ `/orders/${ order.id }` } className="orders-card orders-card--link" key={ order.id }>
                                    <div className="orders-card-header">
                                        <span className="orders-card-id">
                                            { t( 'orders.orderNumber', { id: order.id } ) }
                                        </span>
                                        <div className="od-status-badges">
                                            <span className={ `orders-card-status ${ statusClass }` }>
                                                { statusText }
                                            </span>
                                            <span className={ `od-payment-badge od-payment-badge--${ order.payment_status }` }>
                                                { t( `orders.paymentStatus.${ order.payment_status }` ) }
                                            </span>
                                        </div>
                                    </div>
                                    <p className="orders-card-date">
                                        { new Date( order.created_at ).toLocaleDateString( lang === 'ar' ? 'ar-EG' : 'en-US' ) }
                                    </p>

                                    <div className="orders-card-items">
                                        { order.items?.map( ( orderItem ) =>
                                        {
                                            const product = orderItem.product || {};
                                            const img = product.images?.[ 0 ]?.image;
                                            return (
                                                <div className="orders-item-row" key={ orderItem.id }>
                                                    { img ? (
                                                        <img src={ img } alt={ product.title || "" } className="orders-item-img" />
                                                    ) : (
                                                        <div className="orders-item-img orders-item-img--placeholder" />
                                                    ) }
                                                    <div className="orders-item-info">
                                                        <p className="orders-item-title">{ product.title }</p>
                                                        <p className="orders-item-sub">
                                                            { orderItem.count } × { Number( orderItem.price ).toFixed( 2 ) } { t( 'currency' ) }
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        } ) }
                                    </div>

                                    <div className="orders-card-total">
                                        <span>{ t( 'orders.total' ) }</span>
                                        <strong>{ Number( order.total ).toFixed( 2 ) } { t( 'currency' ) }</strong>
                                    </div>
                                </Link>
                            );
                        } ) }
                    </div>
                ) }
            </div>
        </div>
    );
}
